import { useRef, useState } from 'react';
import type { Playlist } from '@blackblack/shared';
import { api, errorMessage, uploadFile } from '../../lib/api';
import { invalidate } from '../../lib/query';
import { useToast } from '../../state/toast';
import { Icon } from '../../components/ui/Icon';
import { Button, Switch, TextArea, TextField } from '../../components/ui/primitives';
import { Sheet } from '../../components/ui/Sheet';
import { Cover } from '../music/Cover';
import styles from './playlists.module.css';

/** Create or edit a playlist: name, description, cover upload, public/private. */
export function PlaylistEditorSheet({ playlist, onClose, onSaved }: { playlist?: Playlist; onClose: () => void; onSaved?: (p: Playlist) => void }) {
  const toast = useToast();
  const [title, setTitle] = useState(playlist?.title ?? '');
  const [description, setDescription] = useState(playlist?.description ?? '');
  const [isPublic, setIsPublic] = useState(playlist?.isPublic ?? true);
  const [cover, setCover] = useState<{ file: File; preview: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const save = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      const coverUploadId = cover ? (await uploadFile(cover.file, 'image')).uploadId : undefined;
      const body = { title: title.trim(), description, isPublic, ...(coverUploadId ? { coverUploadId } : {}) };
      const saved = playlist ? await api.patch<Playlist>(`/playlists/${playlist.id}`, body) : await api.post<Playlist>('/playlists', body);
      invalidate('playlists:');
      invalidate(`playlist:${saved.id}`);
      toast(playlist ? 'Playlist bijgewerkt' : 'Playlist gemaakt', { tone: 'success' });
      onSaved?.(saved);
      onClose();
    } catch (e) {
      toast(errorMessage(e), { tone: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open onClose={onClose} title={playlist ? 'Playlist bewerken' : 'Nieuwe playlist'}>
      <div className={styles.editor}>
        <button className={styles.coverPick} onClick={() => fileInput.current?.click()} aria-label="Cover kiezen">
          <Cover src={cover?.preview ?? playlist?.coverUrl} size={120} radius={14} />
          <span className={styles.coverPickBadge}>
            <Icon name="image" size={16} />
          </span>
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) setCover({ file: f, preview: URL.createObjectURL(f) });
          }}
        />
        <TextField label="Naam" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} placeholder="Mijn playlist" autoFocus={!playlist} />
        <TextArea label="Beschrijving" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={300} rows={2} placeholder="Waar gaat deze playlist over?" />
        <Switch checked={isPublic} onChange={setIsPublic} label="Openbaar" description={isPublic ? 'Iedereen kan deze playlist zien en volgen' : 'Alleen jij kunt deze playlist zien'} />
        <Button block loading={busy} disabled={!title.trim()} onClick={save}>
          {playlist ? 'Opslaan' : 'Playlist maken'}
        </Button>
      </div>
    </Sheet>
  );
}
